import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { CourtDto } from "../../../models/court/dto/court.dto";
import { classifyRead } from "../../_shared/readState";

export const GET_COURTS_QUERY_KEY = ['courts'];
export const GET_LOCATION_COURTS_QUERY_KEY = (locationId: string) => [locationId, 'courts'];

/**
 * The location's courts, from the ANONYMOUS list rail.
 *
 * It deliberately no longer primes `GET_COURT_QUERY_KEY(court.id)` with the
 * objects it receives. `GET /courts?locationId=` is unguarded and therefore
 * omits `availabilityTemplates` — the per-capacity price grid — while the
 * ADMIN-gated `GET /courts/:id` still returns it. Priming the single-court key
 * from this response made the list object a permanent substitute for the
 * guarded one (`useGetCourt` sets `staleTime: Infinity`, so the seeded entry is
 * never refetched), which would leave `AddAvailabilitiesModal` seeding its form
 * from an empty template list and showing the operator an empty schedule for a
 * court that has one.
 *
 * The cost is one extra request per court-details page — a guarded read the
 * dashboard is entitled to make, on a page that already issues several.
 *
 * GATED ON `locationId` since run 134 (R110-6 / R133-4). Before that this hook
 * carried no `enabled:` at all, so every one of its sixteen call sites issued
 * `GET courts?locationId=undefined` (or `?locationId=`) whenever no club was
 * selected — which for a multi-location org is the whole session until the
 * operator picks one, because `useSelectedLocation` starts `undefined` and only
 * `useSingleClubAutoSelect` fills it in, and only for single-club orgs.
 *
 * The request did not fail. `organization_location.id` is `varchar(255)`, so
 * `location_id = 'undefined'` matches nothing and the API answers **200 with an
 * empty array** — a read that was never meaningfully issued, arriving as a
 * definitive "this venue has no courts". That is the campaign's whole defect
 * class with no error anywhere to report it: the courts page showed its
 * first-use "create your first court" empty state to an operator who had simply
 * not chosen a club, the onboarding checklist marked `setup_courts` incomplete
 * for a venue full of courts, and five pickers offered an empty Select.
 *
 * Because `cacheTime` is `Infinity` the bogus `[]` was also cached for the
 * session — under `[null, 'courts']` / `['', 'courts']`, distinct from any real
 * location's key, so it never poisoned a real answer.
 */
export const useGetCourts = (locationId: string) => {
    const client = useApiClient();
    const query = useQuery({
        /*
         * `.trim()` rather than a bare `Boolean` (R134-5, run 135). Run 134's
         * own coverage matrix flagged that a whitespace-only id satisfies
         * `Boolean` and judged it unreachable from `selectedLocation.id`, which
         * is still true — this is the boundary closed rather than a live defect.
         * It is worth one expression because the failure it prevents is silent
         * in the same way the original was: `courts?locationId=%20` matches no
         * `organization_location.id` and answers 200 with `[]`, so a padded id
         * would arrive as a definitive "this venue has no courts" rather than as
         * an error anyone could see.
         */
        enabled: Boolean(locationId && locationId.trim()),
        staleTime: Infinity,
        cacheTime: Infinity,
        queryKey: GET_LOCATION_COURTS_QUERY_KEY(locationId),
        queryFn: () => client.fetchApi<CourtDto[], {}>({ endpoint: `courts?locationId=${locationId}`, method: 'GET' }),
    });

    return {
        ...query,
        /**
         * Which of the four `ReadState`s this read is in — classified HERE so
         * that all sixteen call sites inherit one answer instead of deriving
         * sixteen (D62/D59), and so that adding the `enabled:` gate above could
         * not leave a caller reading a flag whose meaning had changed under it.
         *
         * Spreading the query result opts this hook's consumers out of React
         * Query v4's tracked-props render optimisation, which is deliberate and
         * costs nothing measurable here: `staleTime` and `cacheTime` are both
         * `Infinity`, so the result object only changes on mount and around an
         * explicit invalidation (`useUpdateCourt`), and `data`/`isLoading`/
         * `isError`/`isFetching` — which the call sites read anyway — already
         * cover every one of those transitions.
         */
        state: classifyRead(query.isError, query.isInitialLoading, query.data),
    };
};